// ============================================================================
// Brand Credits — grant wallet credit to a brand, and the log of every grant
// ============================================================================
// The one screen in the admin dashboard that mints money. Everything about its
// shape follows from that:
//
//   * The grant form only exists for an admin holding the 'credits.grant'
//     permission. Everyone else gets the read-only log and a plain statement of
//     why they can't grant — hiding the page would just make the permission
//     invisible.
//   * Every limit shown here (per-grant cap, rolling 24h headroom, self-dealing)
//     comes from admin_my_credit_grant_context(), the same SQL that enforces it.
//     Nothing on this page decides whether a grant is allowed; the database
//     re-checks all of it and a refusal comes back naming the rule.
//   * The amount is entered in dollars but sent in cents, parsed strictly — a
//     malformed amount is a typo, and this is the worst screen to round one.
//   * One idempotency key per opened form. A double-click, a retried request or
//     a stale tab replays into a no-op instead of granting twice.
//   * Both outcomes are audited: a successful grant and a refused one.
//
// The credit itself lands through admin_grant_brand_credit → wallet_credit_brand,
// so it shows up in the brand's own wallet history with the reason typed here.
// See creator-app/supabase/migrations/20260813000000_admin_roles_and_credit_grants.sql
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Wallet, ShieldCheck, ShieldAlert, Search, Plus, Loader2, Building2,
  ArrowUpRight, Users, Ban, Info, Terminal, X, Gauge, TrendingDown, Minus,
  ShoppingCart, AlertTriangle, RotateCcw, Clock, Video, CalendarRange, XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { DataPagination } from '@/components/DataPagination';

import { usd } from '@/lib/refunds';
import { logCreate } from '@/lib/audit-log';
import {
  fetchCreditGrantContext, fetchCreditGrantStats, fetchCreditGrantLog, fetchGrantingAdmins,
  fetchAdminRoster, searchCreditBrands, grantBrandCredit, parseDollarsToCents,
  GRANT_STATUS_LABEL,
  type BrandCreditOption, type CreditGrantRow, type CreditGrantStatus, type CreditGrantContext,
  // Deduction — the removal half. Separate permission, separate caps, separate
  // log table; nothing above this import was touched to add it.
  fetchCreditDeductContext, fetchCreditDeductStats, fetchCreditDeductionLog, fetchDeductingAdmins,
  deductBrandCredit, DEDUCT_STATUS_LABEL, TRANSACTION_PLATFORM_OPTIONS, OUT_OF_POCKET_PLATFORM,
  type CreditDeductionRow, type CreditDeductStatus, type CreditDeductContext,
  // Brand-PURCHASED credit. Read-only: a top-up is created by the brand and
  // completed by the Whop webhook, so there is nothing here for an admin to do
  // but look — see the note on fetchCreditTopupLog.
  fetchCreditTopupLog, fetchCreditTopupStats, TOPUP_STATUS_LABEL,
  TOPUP_PROCESSING_EXPECTED_DAYS, topupAgeDays,
  type CreditTopupRow, type TopupStatus,
  // Auto refunds — also read-only, same reasoning as top-ups: the SYSTEM
  // decided these, not an admin, so there is nothing to approve, only to see.
  fetchAutoRefundLog, fetchAutoRefundStats,
  type AutoRefundRow,
} from '@/lib/adminCredits';

// Quick-pick amounts. Deliberately modest: the common grant is a make-good, and
// a one-tap $10,000 button on a money screen is an accident waiting to happen.
const QUICK_AMOUNTS = [100, 250, 500, 1000];

const TOPUP_STATUS_STYLE: Record<TopupStatus, string> = {
  credited:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  // Blue, not amber: an ACH clearing is healthy and expected, not something
  // stalled that needs chasing.
  processing: 'bg-sky-50 text-sky-700 border-sky-200',
  failed:    'bg-slate-50 text-slate-600 border-slate-200',
  // Red, not grey: a reversal may have cost BMC real money.
  reversed:  'bg-rose-50 text-rose-700 border-rose-200',
  abandoned: 'bg-slate-50 text-slate-600 border-slate-200',
};

const STATUS_STYLE: Record<CreditGrantStatus, string> = {
  applied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  denied: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

// Identical values to STATUS_STYLE, kept as its own constant so the deduct
// section never shares a binding whose meaning a future grant-only change
// could silently shift.
const DEDUCT_STATUS_STYLE: Record<CreditDeductStatus, string> = {
  applied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  denied: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

// ── Small presentational pieces ─────────────────────────────────────────────

/** Per-accent styling for the KPI tiles — same treatment as HybridLedger's
 *  LedgerStatCard (corner wash + tinted icon chip + tinted value), so every
 *  stat row across the admin reads as one system rather than two card styles.
 *  `indigo`/`slate` are the quiet, non-alarming pair, added for the auto-refund
 *  panel: an automatic refund is the platform working correctly, not a fault,
 *  so it deliberately avoids the amber this file uses for "worth a look" and
 *  the red it uses for outright failure. `emerald` is for money actually
 *  collected (top-ups), mirroring the ledger's "approved" green. */
const STAT_TILE_ACCENTS = {
  default: {
    tile:  'bg-muted text-muted-foreground',
    value: 'text-foreground',
    glow:  'from-foreground/[0.05]',
  },
  violet: {
    tile:  'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    value: 'text-violet-700 dark:text-violet-400',
    glow:  'from-violet-500/[0.10]',
  },
  amber: {
    tile:  'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    value: 'text-amber-700 dark:text-amber-400',
    glow:  'from-amber-500/[0.10]',
  },
  indigo: {
    tile:  'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    value: 'text-indigo-700 dark:text-indigo-400',
    glow:  'from-indigo-500/[0.10]',
  },
  slate: {
    tile:  'bg-slate-500/10 text-slate-600 dark:text-slate-300',
    value: 'text-foreground',
    glow:  'from-slate-500/[0.08]',
  },
  emerald: {
    tile:  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
    glow:  'from-emerald-500/[0.10]',
  },
} as const;

/**
 * One KPI tile.
 *
 * `icon` is optional — a tile without one just skips the chip rather than
 * sprouting a placeholder glyph nobody asked for.
 */
function StatTile({ label, value, hint, tone = 'default', icon: Icon }: {
  label: string; value: string; hint?: string;
  tone?: keyof typeof STAT_TILE_ACCENTS;
  icon?: LucideIcon;
}) {
  const a = STAT_TILE_ACCENTS[tone];
  return (
    <div className="relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm">
      {/* Soft corner wash so the card reads as a panel, not a plain box */}
      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl`} />
      <div className="relative space-y-1.5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          {Icon && (
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.tile}`}>
              <Icon className="h-4 w-4" />
            </span>
          )}
        </div>
        <p className={`text-3xl font-bold leading-none tracking-tight tabular-nums ${a.value}`}>{value}</p>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <div className="text-sm font-medium break-words">{children ?? '—'}</div>
    </div>
  );
}

/** Rolling-24h headroom as a bar. The number that matters is what's LEFT, so
 *  that's the one rendered large.
 *
 *  `verb`/`accent` are optional so the ONE existing call site (grant) renders
 *  byte-for-byte identically without passing them — added for the deduct
 *  section's reuse, not a change to how the grant panel already looks. */
function LimitMeter({ used, limit, verb = 'granted', accent = 'violet' }: {
  used: number; limit: number; verb?: string; accent?: 'violet' | 'red';
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const remaining = Math.max(0, limit - used);
  const tight = pct >= 80;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Gauge className="h-3.5 w-3.5" /> Rolling 24h allowance
        </span>
        <span className={`text-sm font-semibold tabular-nums ${tight ? 'text-amber-700' : ''}`}>
          {usd(remaining)} left
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${tight ? 'bg-amber-500' : accent === 'red' ? 'bg-red-500' : 'bg-violet-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-[11px] text-muted-foreground">
        {usd(used)} {verb} of {usd(limit)} — frees up as earlier {verb === 'granted' ? 'grants' : 'deductions'} age past 24h
      </p>
    </div>
  );
}

// ── Brand picker ────────────────────────────────────────────────────────────

/**
 * Search-as-you-type over brands, showing each one's live balance so ops can see
 * what they're adding to before they pick. Debounced because it hits an RPC.
 */
function BrandPicker({ selected, onSelect }: {
  selected: BrandCreditOption | null;
  onSelect: (b: BrandCreditOption | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data: results, isFetching } = useQuery({
    queryKey: ['credit-brand-search', debounced],
    queryFn: () => searchCreditBrands(debounced, 20),
    enabled: open,
  });

  if (selected) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg border bg-muted/30 p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-semibold">{selected.brand_name ?? 'Unnamed brand'}</span>
            {selected.plan_tier && (
              <Badge variant="outline" className="capitalize text-[10px]">{selected.plan_tier}</Badge>
            )}
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground">{selected.contact_email ?? selected.brand_id}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span>Balance <strong className="tabular-nums">{usd(selected.balance_cents)}</strong></span>
            {selected.held_cents > 0 && (
              <span className="text-muted-foreground">Reserved <strong className="tabular-nums">{usd(selected.held_cents)}</strong></span>
            )}
            <span className="text-muted-foreground">
              Granted to date <strong className="tabular-nums">{usd(selected.lifetime_granted_cents)}</strong>
            </span>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={() => { onSelect(null); setTerm(''); }}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder="Search brands by name, contact email, or ID…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => setOpen(true)}
        className="pl-9"
      />
      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border bg-popover shadow-lg">
          {isFetching && (
            <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}
          {!isFetching && (results?.length ?? 0) === 0 && (
            <p className="p-3 text-xs text-muted-foreground">No brands match “{debounced}”.</p>
          )}
          {results?.map((b) => (
            <button
              key={b.brand_id}
              type="button"
              onClick={() => { onSelect(b); setOpen(false); }}
              className="flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted/60"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{b.brand_name ?? 'Unnamed brand'}</p>
                <p className="truncate text-[11px] text-muted-foreground">{b.contact_email ?? b.brand_id}</p>
              </div>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{usd(b.balance_cents)}</span>
            </button>
          ))}
        </div>
      )}
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
    </div>
  );
}

// ── Grant panel ─────────────────────────────────────────────────────────────

function GrantPanel({ context, onGranted }: {
  context: CreditGrantContext;
  onGranted: () => void;
}) {
  const [brand, setBrand] = useState<BrandCreditOption | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [platform, setPlatform] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per form session. Reset only after a grant lands, so every retry of
  // THIS grant collapses into the same row and a deliberate second grant to the
  // same brand gets its own key.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  const { limits } = context;
  const parsed = parseDollarsToCents(amount);
  const amountCents = parsed.cents;

  // Live brand context, so the balance and self-dealing check reflect the brand
  // actually selected rather than whatever the page loaded with.
  const { data: brandContext } = useQuery({
    queryKey: ['credit-grant-context', brand?.brand_id],
    queryFn: () => fetchCreditGrantContext(brand?.brand_id ?? null),
    enabled: !!brand,
  });
  const liveBrand = brandContext?.brand ?? null;
  const balanceCents = liveBrand?.balance_cents ?? brand?.balance_cents ?? 0;
  const selfDealing = liveBrand?.is_member_of_brand === true;
  const overDaily = amountCents > limits.remaining_24h_cents;
  const belowMin = amountCents > 0 && amountCents < limits.min_grant_cents;
  // BMC funding itself has no external payment to point at — the DB
  // (admin_grant_brand_credit) drops the transaction ID requirement for this
  // one platform only. Every other platform keeps it mandatory.
  const isOutOfPocket = platform === OUT_OF_POCKET_PLATFORM;

  const blocker = !brand
    ? 'Pick a brand to credit'
    : selfDealing
      ? `You’re a member of ${liveBrand?.brand_name ?? 'this brand'}’s team — another admin has to issue this credit.`
      : parsed.error
        ? parsed.error
        : amountCents <= 0
          ? 'Enter an amount'
          : belowMin
            ? `Minimum grant is ${usd(limits.min_grant_cents)}`
            : overDaily
              ? `Over your remaining 24h allowance of ${usd(limits.remaining_24h_cents)}`
              : reason.trim().length < 3
                  ? 'Add a reason — the brand sees it on their wallet history'
                  : reason.trim().length > 500
                    ? 'Reason must be 500 characters or fewer'
                    // Mandatory — credit can only be granted for money that
                    // moved somewhere real; the database refuses without both.
                    : !platform
                    ? 'Pick the payment method this credit corresponds to'
                    : (!isOutOfPocket && !transactionId.trim())
                      ? 'Enter the transaction ID for that payment'
                      : null;

  const mutation = useMutation({
    mutationFn: () => grantBrandCredit({
      brandId: brand!.brand_id,
      amountCents,
      reason: reason.trim(),
      transactionPlatform: platform,
      transactionId: transactionId.trim(),
      internalNote: note.trim() || undefined,
      idempotencyKey: idemKey,
    }),
    onSuccess: (result) => {
      logCreate('brand_credit_grants', result.grant_id, {
        brand_id: result.brand_id,
        amount_cents: result.granted_cents,
        reason: reason.trim(),
        transaction_platform: result.transaction_platform,
        transaction_id: result.transaction_id,
        internal_note: note.trim() || null,
        ledger_entry_id: result.ledger_id,
        balance_after_cents: result.balance_cents,
      }, {
        status: 'success',
        brand_name: result.brand_name,
        replayed: result.replayed,
        description: `Granted ${usd(result.granted_cents)} wallet credit to ${result.brand_name ?? result.brand_id}${result.replayed ? ' (replay — no new credit)' : ''}`,
      });

      toast.success(
        result.replayed
          ? 'That grant had already been applied — no new credit was created.'
          : `${usd(result.granted_cents)} credited to ${result.brand_name ?? 'the brand'}`,
        { description: `New balance ${usd(result.balance_cents)}` },
      );

      setConfirming(false);
      setBrand(null);
      setAmount('');
      setReason('');
      setPlatform('');
      setTransactionId('');
      setNote('');
      setError(null);
      setIdemKey(crypto.randomUUID());
      onGranted();
    },
    onError: (e: any) => {
      const message = e?.message ?? 'Credit grant failed';
      // Refusals are already logged server-side (they're the row the DB couldn't
      // write), but the admin-side trail has to show the attempt too — otherwise
      // the audit log implies nothing happened.
      logCreate('brand_credit_grants', brand?.brand_id ?? 'unknown', {
        brand_id: brand?.brand_id,
        amount_cents: amountCents,
        reason: reason.trim(),
        transaction_platform: platform || null,
        transaction_id: transactionId.trim() || null,
      }, {
        status: 'failed',
        error: message,
        brand_name: brand?.brand_name,
        description: `Credit grant of ${usd(amountCents)} to ${brand?.brand_name ?? brand?.brand_id} was rejected`,
      });
      setError(message);
      setConfirming(false);
    },
  });

  return (
    <Card className="border-violet-200/70">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-4 w-4 text-violet-600" />
            Grant account credit
          </CardTitle>
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
            <ShieldCheck className="mr-1 h-3 w-3" />
            {context.admin?.role_label ?? context.admin?.role}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Adds spendable wallet credit. It is applied to the brand’s next hire before their card —
          the same money a deadline refund produces, so treat it as cash out the door.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Brand</label>
              <BrandPicker selected={brand} onSelect={(b) => { setBrand(b); setError(null); }} />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="bc-amount" className="text-sm font-medium">Amount</label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-40">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    id="bc-amount"
                    value={amount}
                    onChange={(e) => { setAmount(e.target.value); setError(null); }}
                    placeholder="250.00"
                    inputMode="decimal"
                    className="pl-6 font-semibold tabular-nums"
                  />
                </div>
                {QUICK_AMOUNTS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setAmount(String(d)); setError(null); }}
                    className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      amountCents === d * 100
                        ? 'border-violet-400 bg-violet-50 text-violet-800'
                        : 'hover:bg-muted/60'
                    }`}
                  >
                    ${d}
                  </button>
                ))}
              </div>
              {parsed.error && <p className="text-xs text-red-500">{parsed.error}</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="bc-reason" className="text-sm font-medium">
                Reason <span className="font-normal text-muted-foreground">— shown to the brand</span>
              </label>
              <Input
                id="bc-reason"
                value={reason}
                onChange={(e) => { setReason(e.target.value.slice(0, 500)); setError(null); }}
                placeholder="Goodwill credit for the delayed campaign launch"
                maxLength={500}
              />
              <p className={`text-[11px] ${reason.trim().length > 500 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {reason.trim().length > 500 ? 'Max 500 characters allowed' : `${reason.length}/500 characters`}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="bc-platform" className="text-sm font-medium">
                  Payment method <span className="font-normal text-muted-foreground">— required</span>
                </label>
                <Select value={platform} onValueChange={(v) => { setPlatform(v); setError(null); }}>
                  <SelectTrigger id="bc-platform"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_PLATFORM_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="bc-txn" className="text-sm font-medium">
                  Transaction ID{' '}
                  <span className="font-normal text-muted-foreground">
                    {isOutOfPocket ? '— optional' : '— required'}
                  </span>
                </label>
                <Input
                  id="bc-txn"
                  value={transactionId}
                  onChange={(e) => { setTransactionId(e.target.value); setError(null); }}
                  placeholder={isOutOfPocket ? 'Optional — e.g. an internal ticket reference' : 'e.g. Whop charge ID, wire reference'}
                  maxLength={200}
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              {isOutOfPocket
                ? "BMC Funding Out of Pocket is the one platform with no external payment to reference — the transaction ID isn't required for it."
                : 'Credit can only be granted for money that moved somewhere real — this is what the database requires it be tied to.'}
            </p>

            <div className="space-y-1.5">
              <label htmlFor="bc-note" className="text-sm font-medium">
                Internal note <span className="font-normal text-muted-foreground">— never shown to the brand (optional)</span>
              </label>
              <Textarea
                id="bc-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Approved by… / ticket reference"
                rows={2}
              />
            </div>
          </div>

          {/* Preview + limits. Sits beside the form so the consequence of the
              amount is visible while it's being typed. */}
          <div className="space-y-4 rounded-xl border bg-muted/20 p-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                After this grant
              </p>
              <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-violet-900">
                {usd(balanceCents + Math.max(0, amountCents))}
              </p>
              <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                {usd(balanceCents)} now
                {amountCents > 0 && <> · <span className="font-semibold text-violet-700">+{usd(amountCents)}</span></>}
              </p>
            </div>

            <LimitMeter used={limits.granted_24h_cents} limit={limits.daily_limit_cents} />

            <p className="text-[11px] text-muted-foreground">
              The rolling 24h limit is enforced in the database, per admin — ask a super admin to
              raise yours if a legitimate grant doesn’t fit.
            </p>
          </div>
        </div>

        {selfDealing && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <Ban className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="text-xs text-red-800">
              <p className="font-semibold">Self-dealing blocked</p>
              <p>
                You hold a seat on this brand’s team, so you can’t credit it. The database refuses this
                combination regardless of your permission — another admin has to issue it.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            <p className="font-semibold">Grant refused</p>
            <p className="mt-0.5">{error}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            disabled={!!blocker || mutation.isPending}
            onClick={() => setConfirming(true)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {amountCents > 0 ? `Credit ${usd(amountCents)}` : 'Credit brand'}
          </Button>
          {blocker && <span className="text-xs text-muted-foreground">{blocker}</span>}
        </div>
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={(o) => !mutation.isPending && setConfirming(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Credit {usd(amountCents)} to {brand?.brand_name ?? 'this brand'}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This adds <strong>{usd(amountCents)}</strong> of spendable credit to the brand’s wallet,
                  taking their balance to <strong>{usd(balanceCents + amountCents)}</strong>. It will be
                  applied to their next hire before their card is charged.
                </p>
                <div className="rounded-md border bg-muted/40 p-3 text-xs">
                  <p className="text-muted-foreground">The brand will see:</p>
                  <p className="mt-1 font-medium text-foreground">“{reason.trim()}”</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Tied to {TRANSACTION_PLATFORM_OPTIONS.find(o => o.value === platform)?.label ?? platform} ·{' '}
                  <span className="font-mono">{transactionId.trim()}</span>.
                </p>
                {/* <p className="text-xs text-muted-foreground">
                  Logged against your account and emailed to the brand. Undoing it needs a SQL reversal
                  (wallet_reverse_entry) — there is no button for that here.
                </p> */}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" disabled={mutation.isPending} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending
                ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Crediting…</>
                : `Confirm — credit ${usd(amountCents)}`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/** Shown to an admin without 'credits.grant'. States the reason plainly and, for
 *  a super admin reading over someone's shoulder, the exact SQL that grants it —
 *  the roster is deliberately not writable from the app. */
function NoPermissionCard({ context }: { context: CreditGrantContext }) {
  return (
    <Card className="border-amber-200/70 bg-gradient-to-br from-amber-50/50 to-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          You can’t grant brand credit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          {context.blocked_reason ?? 'Your account does not hold the credits.grant permission.'}
        </p>
        {/* <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Your admin role">
            {context.admin
              ? <Badge variant="outline" className="capitalize">{context.admin.role_label ?? context.admin.role}</Badge>
              : <span className="text-muted-foreground">Not on the credit roster</span>}
          </Field>
          <Field label="Permissions">
            {context.permissions.length
              ? context.permissions.map(p => <Badge key={p} variant="secondary" className="mr-1 font-mono text-[10px]">{p}</Badge>)
              : <span className="text-muted-foreground">None</span>}
          </Field>
          <Field label="Auth admin flag">
            {context.is_auth_admin
              ? <span className="text-emerald-700">Present</span>
              : <span className="text-red-600">Missing</span>}
          </Field>
        </div> */}
        {/* <div className="rounded-lg border bg-muted/40 p-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold">
            <Terminal className="h-3.5 w-3.5" /> Granting this permission
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            The admin roster has no write path through the app — on purpose. A super admin runs this in
            the Supabase SQL editor:
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-background p-2 text-[11px] leading-relaxed">
{`SELECT public.admin_grant_role_by_email(
  'admin@brandsmeetcreators.com', 'finance_admin');`}
          </pre>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            The account must already have <code className="font-mono">app_metadata.is_admin = true</code> in
            Supabase Auth, or the insert is refused.
          </p>
        </div> */}
        <p className="text-xs text-muted-foreground">
          The log below is visible to every admin — credit movements are not a secret, only the ability
          to create them is restricted.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Grant log ───────────────────────────────────────────────────────────────

function GrantLog() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<CreditGrantStatus | 'all'>('all');
  const [admin, setAdmin] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<CreditGrantRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: admins } = useQuery({ queryKey: ['credit-grant-admins'], queryFn: fetchGrantingAdmins });

  const { data, isLoading } = useQuery({
    queryKey: ['credit-grant-log', page, pageSize, debounced, status, admin],
    queryFn: () => fetchCreditGrantLog({
      page, pageSize,
      search: debounced,
      status,
      adminEmail: admin === 'all' ? null : admin,
    }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Brand, admin email, or reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v as any); setPage(1); }}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
            <SelectItem value="denied">Refused</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={admin} onValueChange={(v) => { setAdmin(v); setPage(1); }}>
          <SelectTrigger><SelectValue placeholder="Granted by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All admins</SelectItem>
            {admins?.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Granted by</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Internal note</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Credited at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                      No credit grants recorded yet.
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelected(row)}>
                    <TableCell className="max-w-[200px]">
                      <span className="block truncate font-medium">{row.brand_name ?? 'Unnamed brand'}</span>
                      {row.balance_after_cents != null && row.status === 'applied' && (
                        <span className="block text-[11px] tabular-nums text-muted-foreground">
                          balance → {usd(row.balance_after_cents)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-semibold tabular-nums ${
                        row.status === 'applied'
                          ? (row.reversed_at ? 'text-muted-foreground line-through' : 'text-violet-700')
                          : 'text-muted-foreground'
                      }`}>
                        +{usd(row.amount_cents)}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[190px]">
                      <span className="block truncate text-sm">{row.admin_email}</span>
                      {row.admin_role && (
                        <span className="block text-[11px] capitalize text-muted-foreground">
                          {row.admin_role.replace(/_/g, ' ')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[260px]">
                      <span className="block truncate text-sm text-muted-foreground">{row.reason}</span>
                      {row.transaction_platform && (
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {TRANSACTION_PLATFORM_OPTIONS.find(o => o.value === row.transaction_platform)?.label ?? row.transaction_platform}
                          {row.transaction_id && <span className="font-mono"> · {row.transaction_id}</span>}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {row.internal_note
                        ? <span className="block truncate text-sm italic text-muted-foreground">{row.internal_note}</span>
                        : <span className="text-sm text-muted-foreground/60">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_STYLE[row.status]}>
                        {GRANT_STATUS_LABEL[row.status]}
                      </Badge>
                      {row.reversed_at && (
                        <span className="mt-1 block text-[11px] text-muted-foreground">reversed</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {format(new Date(row.created_at), 'MMM d, yyyy')}
                      <span className="block text-[11px] text-muted-foreground">
                        {format(new Date(row.created_at), 'h:mm a')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {total > 0 && (
            <div className="mt-4">
              <DataPagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Credit grant</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                <p className="text-xs text-violet-700">Amount</p>
                <p className="text-3xl font-bold tabular-nums text-violet-900">+{usd(selected.amount_cents)}</p>
                <p className="mt-1 text-xs text-violet-700">
                  {selected.brand_name ?? 'Unnamed brand'} ·{' '}
                  {format(new Date(selected.created_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Outcome">
                  <Badge variant="outline" className={STATUS_STYLE[selected.status]}>
                    {GRANT_STATUS_LABEL[selected.status]}
                  </Badge>
                </Field>
                <Field label="Granted by">{selected.admin_email}</Field>
                <Field label="Admin role">
                  <span className="capitalize">{selected.admin_role?.replace(/_/g, ' ') ?? '—'}</span>
                </Field>
                <Field label="Balance before">
                  {selected.balance_before_cents != null ? usd(selected.balance_before_cents) : '—'}
                </Field>
                <Field label="Balance after">
                  {selected.balance_after_cents != null ? usd(selected.balance_after_cents) : '—'}
                </Field>
                <Field label="Brand ID">
                  <span className="font-mono text-[11px]">{selected.brand_id}</span>
                </Field>
                <Field label="Payment method">
                  {selected.transaction_platform
                    ? (TRANSACTION_PLATFORM_OPTIONS.find(o => o.value === selected.transaction_platform)?.label ?? selected.transaction_platform)
                    : '—'}
                </Field>
              </div>

              <Field label="Reason (shown to the brand)">
                <p className="rounded-md border bg-muted/40 p-2 text-sm font-normal">{selected.reason}</p>
              </Field>

              {selected.transaction_id && (
                <Field label="Transaction ID">
                  <span className="font-mono text-xs">{selected.transaction_id}</span>
                </Field>
              )}

              {selected.internal_note && (
                <Field label="Internal note (never shown to the brand)">
                  <p className="rounded-md border bg-muted/40 p-2 text-sm font-normal">{selected.internal_note}</p>
                </Field>
              )}

              {selected.error_message && (
                <Field label="Why it was refused">
                  <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm font-normal text-amber-900">
                    {selected.error_message}
                  </p>
                </Field>
              )}

              {selected.reversed_at && (
                <Field label="Reversed">
                  <span>
                    {format(new Date(selected.reversed_at), 'MMM d, yyyy')}
                    {selected.reversed_by && <> by {selected.reversed_by}</>}
                  </span>
                </Field>
              )}

              <div className="grid grid-cols-1 gap-3 border-t pt-4">
                <Field label="Wallet ledger entry">
                  <span className="font-mono text-[11px]">{selected.ledger_entry_id ?? '— (no money moved)'}</span>
                </Field>
                <Field label="Idempotency key">
                  <span className="font-mono text-[11px] break-all">{selected.idempotency_key}</span>
                </Field>
                <Field label="Request origin">
                  <span className="text-xs font-normal text-muted-foreground">
                    {selected.request_ip ?? 'unknown IP'}
                    {selected.user_agent && <> · {selected.user_agent}</>}
                  </span>
                </Field>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Permission roster ───────────────────────────────────────────────────────

function PermissionRoster() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-roster'], queryFn: fetchAdminRoster });

  return (
    <div className="space-y-4">
      {/* <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Read-only. <code className="font-mono">admin_users</code> has no write policy and no API path —
          seats are added or revoked from the Supabase SQL editor with{' '}
          <code className="font-mono">admin_grant_role_by_email()</code> /{' '}
          <code className="font-mono">admin_revoke_admin_user()</code>. Only accounts whose{' '}
          <code className="font-mono">app_metadata.is_admin</code> is true can be seated, and clearing that
          flag disables the row immediately.
        </p>
      </div> */}

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Admin</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className="text-right">Per 24h</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!isLoading && (data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      No admins seated yet.
                    </TableCell>
                  </TableRow>
                )}
                {data?.map((a) => (
                  <TableRow key={a.user_id}>
                    <TableCell>
                      <span className="block font-medium">{a.display_name ?? a.email}</span>
                      {a.display_name && <span className="block text-[11px] text-muted-foreground">{a.email}</span>}
                      {a.notes && <span className="block text-[11px] italic text-muted-foreground">{a.notes}</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{a.role.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell>
                      {a.permissions.length
                        ? a.permissions.map(p => (
                            <Badge key={p} variant="secondary" className="mr-1 font-mono text-[10px]">{p}</Badge>
                          ))
                        : <span className="text-xs text-muted-foreground">role defaults only</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {a.daily_grant_limit_cents != null ? usd(a.daily_grant_limit_cents) : <span className="text-muted-foreground">default</span>}
                    </TableCell>
                    <TableCell>
                      {a.is_active
                        ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Active</Badge>
                        : <Badge variant="outline" className="text-muted-foreground">Revoked</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Deduct panel ─────────────────────────────────────────────────────────────
// The removal counterpart of GrantPanel, above — reuses BrandPicker (generic,
// unrelated to grant-vs-deduct) but nothing else. Styled red/destructive so it
// is never confusable with the grant card above it, and gates on 'credits.deduct'
// — a completely independent permission from 'credits.grant'.

function DeductPanel({ context, onDeducted }: {
  context: CreditDeductContext;
  onDeducted: () => void;
}) {
  const [brand, setBrand] = useState<BrandCreditOption | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [platform, setPlatform] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  const { limits } = context;
  const parsed = parseDollarsToCents(amount);
  const amountCents = parsed.cents;

  // Live brand context — the deduct-specific read (available_cents is the hard
  // ceiling here, unlike a grant which has none).
  const { data: brandContext } = useQuery({
    queryKey: ['credit-deduct-context', brand?.brand_id],
    queryFn: () => fetchCreditDeductContext(brand?.brand_id ?? null),
    enabled: !!brand,
  });
  const liveBrand = brandContext?.brand ?? null;
  const balanceCents = liveBrand?.balance_cents ?? brand?.balance_cents ?? 0;
  const availableCents = liveBrand?.available_cents ?? brand?.available_cents ?? 0;
  const selfDealing = liveBrand?.is_member_of_brand === true;

  const overAvailable = amountCents > availableCents;
  const overDaily = amountCents > limits.remaining_24h_cents;
  const belowMin = amountCents > 0 && amountCents < limits.min_deduct_cents;

  const blocker = !brand
    ? 'Pick a brand to deduct from'
    : selfDealing
      ? `You're a member of ${liveBrand?.brand_name ?? 'this brand'}'s team — another admin has to issue this deduction.`
      : parsed.error
        ? parsed.error
        : amountCents <= 0
          ? 'Enter an amount'
          : belowMin
            ? `Minimum deduction is ${usd(limits.min_deduct_cents)}`
            : overAvailable
              ? `Only ${usd(availableCents)} is spendable on this brand right now`
              : overDaily
                ? `Over your remaining 24h allowance of ${usd(limits.remaining_24h_cents)}`
                : reason.trim().length < 3
                  ? 'Add a reason — the brand sees it on their wallet history'
                  : reason.trim().length > 500
                    ? 'Reason must be 500 characters or fewer'
                    : null;

  const mutation = useMutation({
    mutationFn: () => deductBrandCredit({
      brandId: brand!.brand_id,
      amountCents,
      reason: reason.trim(),
      transactionPlatform: platform || undefined,
      transactionId: transactionId.trim() || undefined,
      internalNote: note.trim() || undefined,
      idempotencyKey: idemKey,
    }),
    onSuccess: (result) => {
      logCreate('brand_credit_deductions', result.deduction_id, {
        brand_id: result.brand_id,
        amount_cents: result.deducted_cents,
        reason: reason.trim(),
        transaction_platform: result.transaction_platform,
        transaction_id: result.transaction_id,
        internal_note: note.trim() || null,
        ledger_entry_id: result.ledger_id,
        balance_after_cents: result.balance_cents,
      }, {
        status: 'success',
        brand_name: result.brand_name,
        replayed: result.replayed,
        description: `Deducted ${usd(result.deducted_cents)} wallet credit from ${result.brand_name ?? result.brand_id}${result.replayed ? ' (replay — no new deduction)' : ''}`,
      });

      toast.success(
        result.replayed
          ? 'That deduction had already been applied — no new debit was created.'
          : `${usd(result.deducted_cents)} deducted from ${result.brand_name ?? 'the brand'}`,
        { description: `New balance ${usd(result.balance_cents)}` },
      );

      setConfirming(false);
      setBrand(null);
      setAmount('');
      setReason('');
      setPlatform('');
      setTransactionId('');
      setNote('');
      setError(null);
      setIdemKey(crypto.randomUUID());
      onDeducted();
    },
    onError: (e: any) => {
      const message = e?.message ?? 'Credit deduction failed';
      logCreate('brand_credit_deductions', brand?.brand_id ?? 'unknown', {
        brand_id: brand?.brand_id,
        amount_cents: amountCents,
        reason: reason.trim(),
        transaction_platform: platform || null,
        transaction_id: transactionId.trim() || null,
      }, {
        status: 'failed',
        error: message,
        brand_name: brand?.brand_name,
        description: `Credit deduction of ${usd(amountCents)} from ${brand?.brand_name ?? brand?.brand_id} was rejected`,
      });
      setError(message);
      setConfirming(false);
    },
  });

  return (
    <Card className="border-red-200/70">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="h-4 w-4 text-red-600" />
            Deduct account credit
          </CardTitle>
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
            <ShieldCheck className="mr-1 h-3 w-3" />
            {context.admin?.role_label ?? context.admin?.role}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Removes spendable wallet credit — the brand's money coming out of their account. Bounded by
          what they can actually give back; credit reserved for an in-flight hire is never touched.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Brand</label>
              <BrandPicker selected={brand} onSelect={(b) => { setBrand(b); setError(null); }} />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="dc-amount" className="text-sm font-medium">Amount</label>
              <div className="relative w-40">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  id="dc-amount"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(null); }}
                  placeholder="50.00"
                  inputMode="decimal"
                  className="pl-6 font-semibold tabular-nums"
                />
              </div>
              {parsed.error && <p className="text-xs text-red-500">{parsed.error}</p>}
              {brand && (
                <p className="text-[11px] text-muted-foreground">
                  Up to {usd(availableCents)} spendable right now.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="dc-reason" className="text-sm font-medium">
                Reason <span className="font-normal text-muted-foreground">— shown to the brand</span>
              </label>
              <Input
                id="dc-reason"
                value={reason}
                onChange={(e) => { setReason(e.target.value.slice(0, 500)); setError(null); }}
                placeholder="Chargeback correction on a prior goodwill credit"
                maxLength={500}
              />
              <p className={`text-[11px] ${reason.trim().length > 500 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {reason.trim().length > 500 ? 'Max 500 characters allowed' : `${reason.length}/500 characters`}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="dc-platform" className="text-sm font-medium">
                  Payment method <span className="font-normal text-muted-foreground">— optional</span>
                </label>
                <Select value={platform} onValueChange={(v) => { setPlatform(v); setError(null); }}>
                  <SelectTrigger id="dc-platform"><SelectValue placeholder="Not tied to one payment" /></SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_PLATFORM_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="dc-txn" className="text-sm font-medium">
                  Transaction ID <span className="font-normal text-muted-foreground">— optional</span>
                </label>
                <Input
                  id="dc-txn"
                  value={transactionId}
                  onChange={(e) => { setTransactionId(e.target.value); setError(null); }}
                  placeholder="e.g. the Whop refund this reconciles"
                  maxLength={200}
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Fill these in when this deduction reconciles a specific external refund. Leave blank for
              a plain balance correction.
            </p>

            <div className="space-y-1.5">
              <label htmlFor="dc-note" className="text-sm font-medium">
                Internal note <span className="font-normal text-muted-foreground">— never shown to the brand (optional)</span>
              </label>
              <Textarea
                id="dc-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Approved by… / ticket reference"
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-xl border bg-muted/20 p-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                After this deduction
              </p>
              <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-red-800">
                {usd(Math.max(0, balanceCents - Math.max(0, amountCents)))}
              </p>
              <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                {usd(balanceCents)} now
                {amountCents > 0 && <> · <span className="font-semibold text-red-700">−{usd(amountCents)}</span></>}
              </p>
            </div>

            <LimitMeter used={limits.deducted_24h_cents} limit={limits.daily_limit_cents} verb="deducted" accent="red" />

            <p className="text-[11px] text-muted-foreground">
              This rolling 24h limit is tracked separately from your grant limit — ask a super admin
              to raise yours if a legitimate deduction doesn't fit.
            </p>
          </div>
        </div>

        {selfDealing && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <Ban className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="text-xs text-red-800">
              <p className="font-semibold">Self-dealing blocked</p>
              <p>
                You hold a seat on this brand's team, so you can't deduct from it. The database refuses
                this combination regardless of your permission — another admin has to issue it.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            <p className="font-semibold">Deduction refused</p>
            <p className="mt-0.5">{error}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="destructive"
            disabled={!!blocker || mutation.isPending}
            onClick={() => setConfirming(true)}
          >
            <Minus className="mr-1.5 h-4 w-4" />
            {amountCents > 0 ? `Deduct ${usd(amountCents)}` : 'Deduct credit'}
          </Button>
          {blocker && <span className="text-xs text-muted-foreground">{blocker}</span>}
        </div>
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={(o) => !mutation.isPending && setConfirming(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deduct {usd(amountCents)} from {brand?.brand_name ?? 'this brand'}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This removes <strong>{usd(amountCents)}</strong> of spendable credit from the brand's
                  wallet, taking their balance to <strong>{usd(Math.max(0, balanceCents - amountCents))}</strong>.
                  This is their money — treat it with the same care as a refund, in reverse.
                </p>
                <div className="rounded-md border bg-muted/40 p-3 text-xs">
                  <p className="text-muted-foreground">The brand will see:</p>
                  <p className="mt-1 font-medium text-foreground">"{reason.trim()}"</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" disabled={mutation.isPending} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending
                ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Deducting…</>
                : `Confirm — deduct ${usd(amountCents)}`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/** Shown to an admin without 'credits.deduct' — including one who holds
 *  'credits.grant', which does NOT imply this permission. */
function NoDeductPermissionCard({ context }: { context: CreditDeductContext }) {
  return (
    <Card className="border-amber-200/70 bg-gradient-to-br from-amber-50/50 to-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          You can't deduct brand credit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          {context.blocked_reason ?? 'Your account does not hold the credits.deduct permission.'}
        </p>
        {/* <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Your admin role">
            {context.admin
              ? <Badge variant="outline" className="capitalize">{context.admin.role_label ?? context.admin.role}</Badge>
              : <span className="text-muted-foreground">Not on the credit roster</span>}
          </Field>
          <Field label="Permissions">
            {context.permissions.length
              ? context.permissions.map(p => <Badge key={p} variant="secondary" className="mr-1 font-mono text-[10px]">{p}</Badge>)
              : <span className="text-muted-foreground">None</span>}
          </Field>
          <Field label="Auth admin flag">
            {context.is_auth_admin
              ? <span className="text-emerald-700">Present</span>
              : <span className="text-red-600">Missing</span>}
          </Field>
        </div> */}
        {/* <div className="rounded-lg border bg-muted/40 p-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold">
            <Terminal className="h-3.5 w-3.5" /> Granting this permission
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            credits.deduct is independent of credits.grant — holding one never implies the other. A
            super admin runs this in the Supabase SQL editor:
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-background p-2 text-[11px] leading-relaxed">
{`SELECT public.admin_grant_role_by_email(
  'admin@brandsmeetcreators.com', 'finance_admin',
  p_permissions => ARRAY['credits.grant', 'credits.deduct']);`}
          </pre>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Optional tighter rolling-24h cap (default is $20,000 per 24h):
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-background p-2 text-[11px] leading-relaxed">
{`UPDATE admin_users SET daily_deduct_limit_cents = 500000
 WHERE email = 'admin@brandsmeetcreators.com';`}
          </pre>
        </div> */}
      </CardContent>
    </Card>
  );
}

// ── Deduct log ────────────────────────────────────────────────────────────────

function DeductLog() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<CreditDeductStatus | 'all'>('all');
  const [admin, setAdmin] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<CreditDeductionRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: admins } = useQuery({ queryKey: ['credit-deduct-admins'], queryFn: fetchDeductingAdmins });

  const { data, isLoading } = useQuery({
    queryKey: ['credit-deduct-log', page, pageSize, debounced, status, admin],
    queryFn: () => fetchCreditDeductionLog({
      page, pageSize,
      search: debounced,
      status,
      adminEmail: admin === 'all' ? null : admin,
    }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Brand, admin email, or reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v as any); setPage(1); }}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
            <SelectItem value="denied">Refused</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={admin} onValueChange={(v) => { setAdmin(v); setPage(1); }}>
          <SelectTrigger><SelectValue placeholder="Deducted by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All admins</SelectItem>
            {admins?.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Deducted by</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Internal note</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Deducted at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                      No credit deductions recorded yet.
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelected(row)}>
                    <TableCell className="max-w-[200px]">
                      <span className="block truncate font-medium">{row.brand_name ?? 'Unnamed brand'}</span>
                      {row.balance_after_cents != null && row.status === 'applied' && (
                        <span className="block text-[11px] tabular-nums text-muted-foreground">
                          balance → {usd(row.balance_after_cents)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-semibold tabular-nums ${
                        row.status === 'applied'
                          ? (row.reversed_at ? 'text-muted-foreground line-through' : 'text-red-700')
                          : 'text-muted-foreground'
                      }`}>
                        −{usd(row.amount_cents)}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[190px]">
                      <span className="block truncate text-sm">{row.admin_email}</span>
                      {row.admin_role && (
                        <span className="block text-[11px] capitalize text-muted-foreground">
                          {row.admin_role.replace(/_/g, ' ')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[260px]">
                      <span className="block truncate text-sm text-muted-foreground">{row.reason}</span>
                      {row.transaction_platform && (
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {TRANSACTION_PLATFORM_OPTIONS.find(o => o.value === row.transaction_platform)?.label ?? row.transaction_platform}
                          {row.transaction_id && <span className="font-mono"> · {row.transaction_id}</span>}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {row.internal_note
                        ? <span className="block truncate text-sm italic text-muted-foreground">{row.internal_note}</span>
                        : <span className="text-sm text-muted-foreground/60">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={DEDUCT_STATUS_STYLE[row.status]}>
                        {DEDUCT_STATUS_LABEL[row.status]}
                      </Badge>
                      {row.reversed_at && (
                        <span className="mt-1 block text-[11px] text-muted-foreground">reversed</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {format(new Date(row.created_at), 'MMM d, yyyy')}
                      <span className="block text-[11px] text-muted-foreground">
                        {format(new Date(row.created_at), 'h:mm a')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {total > 0 && (
            <div className="mt-4">
              <DataPagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Credit deduction</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-xs text-red-700">Amount</p>
                <p className="text-3xl font-bold tabular-nums text-red-900">−{usd(selected.amount_cents)}</p>
                <p className="mt-1 text-xs text-red-700">
                  {selected.brand_name ?? 'Unnamed brand'} ·{' '}
                  {format(new Date(selected.created_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Outcome">
                  <Badge variant="outline" className={DEDUCT_STATUS_STYLE[selected.status]}>
                    {DEDUCT_STATUS_LABEL[selected.status]}
                  </Badge>
                </Field>
                <Field label="Deducted by">{selected.admin_email}</Field>
                <Field label="Admin role">
                  <span className="capitalize">{selected.admin_role?.replace(/_/g, ' ') ?? '—'}</span>
                </Field>
                <Field label="Balance before">
                  {selected.balance_before_cents != null ? usd(selected.balance_before_cents) : '—'}
                </Field>
                <Field label="Balance after">
                  {selected.balance_after_cents != null ? usd(selected.balance_after_cents) : '—'}
                </Field>
                <Field label="Brand ID">
                  <span className="font-mono text-[11px]">{selected.brand_id}</span>
                </Field>
                <Field label="Payment method">
                  {selected.transaction_platform
                    ? (TRANSACTION_PLATFORM_OPTIONS.find(o => o.value === selected.transaction_platform)?.label ?? selected.transaction_platform)
                    : '—'}
                </Field>
              </div>

              <Field label="Reason (shown to the brand)">
                <p className="rounded-md border bg-muted/40 p-2 text-sm font-normal">{selected.reason}</p>
              </Field>

              {selected.transaction_id && (
                <Field label="Transaction ID">
                  <span className="font-mono text-xs">{selected.transaction_id}</span>
                </Field>
              )}

              {selected.internal_note && (
                <Field label="Internal note (never shown to the brand)">
                  <p className="rounded-md border bg-muted/40 p-2 text-sm font-normal">{selected.internal_note}</p>
                </Field>
              )}

              {selected.error_message && (
                <Field label="Why it was refused">
                  <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm font-normal text-amber-900">
                    {selected.error_message}
                  </p>
                </Field>
              )}

              {selected.reversed_at && (
                <Field label="Reversed">
                  <span>
                    {format(new Date(selected.reversed_at), 'MMM d, yyyy')}
                    {selected.reversed_by && <> by {selected.reversed_by}</>}
                  </span>
                </Field>
              )}

              <div className="grid grid-cols-1 gap-3 border-t pt-4">
                <Field label="Wallet ledger entry">
                  <span className="font-mono text-[11px]">{selected.ledger_entry_id ?? '— (no money moved)'}</span>
                </Field>
                <Field label="Idempotency key">
                  <span className="font-mono text-[11px] break-all">{selected.idempotency_key}</span>
                </Field>
                <Field label="Request origin">
                  <span className="text-xs font-normal text-muted-foreground">
                    {selected.request_ip ?? 'unknown IP'}
                    {selected.user_agent && <> · {selected.user_agent}</>}
                  </span>
                </Field>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

/**
 * Brand-purchased credit. READ ONLY — and that is the point worth stating on
 * screen as well as in code: nobody here approves a top-up. The brand starts it,
 * Whop takes the money, and the signature-verified webhook credits the wallet.
 * There is no admin button in this flow because giving one would reintroduce
 * exactly the risk the design removes.
 *
 * What ops actually needs from this table is two answers: "did this brand's
 * payment land as credit" and "how much have we lost to chargebacks on credit
 * that was already spent". The shortfall tile is the second one.
 */
function TopupLog() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<TopupStatus | 'all'>('all');
  // Abandoned rows are real history — a brand that opened a checkout and never
  // paid. They are hidden by DEFAULT rather than withheld, because opening the
  // tier picker writes a row and a brand comparing prices leaves several. When
  // support is reconstructing "what did this brand actually try", that noise is
  // exactly what they need, so it is one click away rather than one status
  // filter they have to know to pick.
  const [includeAbandoned, setIncludeAbandoned] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: stats } = useQuery({
    queryKey: ['credit-topup-stats'],
    queryFn: () => fetchCreditTopupStats(30),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['credit-topup-log', page, pageSize, debounced, status, includeAbandoned],
    // Opening the tier picker writes a row, so a brand comparing prices leaves
    // rows that were never purchases — hidden by default so they don't bury the
    // real ones. The checkbox brings them back, and picking the Abandoned status
    // still shows them regardless (fetchCreditTopupLog only applies
    // hideAbandoned when no explicit status was chosen).
    queryFn: () => fetchCreditTopupLog({
      page, pageSize, search: debounced, status, hideAbandoned: !includeAbandoned,
    }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      {/* Unrecoverable chargebacks get their own banner rather than a tile:
          it is the only number on this screen that needs someone to act. */}
      {!!stats?.shortfallCents && stats.shortfallCents > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
          <div className="text-sm">
            <p className="font-semibold text-rose-800">
              {usd(stats.shortfallCents)} of purchased credit was spent before the payment was reversed
            </p>
            <p className="mt-0.5 text-xs text-rose-700">
              Last 30 days. This credit had already been spent on hires when the refund or chargeback
              arrived, so it could not be clawed back automatically — the brands below marked
              &ldquo;Refunded / charged back&rdquo; with a shortfall need chasing.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        {/* "Sold" is money COLLECTED, which stopped being the same number as
            credit issued once promo codes could cover a purchase: a 100%-off
            code issues the full tier and collects nothing. Both are shown so
            neither reads as the other. Identical output when no promo was used. */}
        <StatTile
          label="Credit sold (30d)"
          value={usd(stats?.collectedCents ?? 0)}
          tone="emerald"
          icon={ShoppingCart}
          hint={
            (stats?.compedCents ?? 0) > 0
              ? `${stats?.creditedCount ?? 0} purchases · ${usd(stats?.creditedCents ?? 0)} credit issued, ${usd(stats?.compedCents ?? 0)} comped by promo`
              : `${stats?.creditedCount ?? 0} purchases`
          }
        />
        {/* Not labelled ACH: a processing payment's rail is unknown until it
            settles, so this counts every authorised-but-unsettled payment,
            card and bank transfer alike. */}
        <StatTile
          label="Processing"
          value={usd(stats?.processingCents ?? 0)}
          tone="indigo"
          icon={Clock}
          hint={`${stats?.processingCount ?? 0} payments authorised, not settled`}
        />
        <StatTile
          label="Failed"
          value={String(stats?.failedCount ?? 0)}
          tone="slate"
          icon={XCircle}
          hint="card declined or abandoned"
        />
        <StatTile
          label="Reversed"
          value={String(stats?.reversedCount ?? 0)}
          tone="amber"
          icon={RotateCcw}
          hint="refunded or charged back"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="relative sm:col-span-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Brand, buyer email, or Whop payment id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v as any); setPage(1); }}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{includeAbandoned ? 'All statuses' : 'All except abandoned'}</SelectItem>
            <SelectItem value="credited">Credited</SelectItem>
            <SelectItem value="pending">Awaiting payment</SelectItem>
            <SelectItem value="processing">Payment processing</SelectItem>
            <SelectItem value="failed">Payment failed</SelectItem>
            <SelectItem value="reversed">Refunded / charged back</SelectItem>
            <SelectItem value="abandoned">Abandoned (never paid)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deliberately outside the status filter: support's question is usually
          "show me everything this brand tried", not "show me only the ones that
          failed". Greyed out while an explicit status is selected, because the
          status wins there and the checkbox would otherwise look broken. */}
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <Checkbox
          checked={includeAbandoned}
          disabled={status !== 'all'}
          onCheckedChange={(v) => { setIncludeAbandoned(v === true); setPage(1); }}
        />
        Include abandoned checkouts (opened but never paid)
        {status !== 'all' && (
          <span className="text-xs">— showing “{TOPUP_STATUS_LABEL[status] ?? status}” only</span>
        )}
      </label>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Bought by</TableHead>
                  <TableHead>Paid with</TableHead>
                  <TableHead>Whop payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                      No credit purchases yet.
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="max-w-[200px]">
                      <span className="block truncate font-medium">{row.brand_name ?? 'Unnamed brand'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {/* The credited figure when there is one, else the tier
                          price — labelled, so a settled amount that differs from
                          the tier (tax, a promo code) is visible rather than
                          silently reconciled. */}
                      <span className={`font-semibold tabular-nums ${
                        row.status === 'credited'
                          ? 'text-emerald-700'
                          : row.status === 'reversed' ? 'text-muted-foreground line-through' : 'text-muted-foreground'
                      }`}>
                        {usd(row.credited_cents ?? row.expected_amount_cents)}
                      </span>
                      {row.credited_cents != null && row.credited_cents !== row.expected_amount_cents && (
                        <span className="block text-[11px] text-amber-700">
                          {row.is_custom_amount ? 'asked for' : 'tier was'} {usd(row.expected_amount_cents)}
                        </span>
                      )}
                      {/* Marks that the brand TYPED this amount rather than
                          picking a tile — not that a plan was created for it. A
                          typed amount matching a listed price reuses that
                          plan. So an unfamiliar figure here is expected rather
                          than a repricing to chase, and a familiar one just
                          means they typed instead of clicking. */}
                      {row.is_custom_amount && (
                        <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                          custom
                        </span>
                      )}
                      {/* Without this, a promo purchase is the most alarming row
                          in the table: full credit issued against a $0 payment,
                          with nothing on screen saying why. Names the code and
                          what was actually collected. */}
                      {!!row.promo_discount_cents && row.promo_discount_cents > 0 && (
                        <span className="block text-[11px] text-violet-700">
                          {row.promo_code ?? 'promo'} covered {usd(row.promo_discount_cents)}
                          {(row.settled_amount_cents ?? 0) > 0
                            ? ` · ${usd(row.settled_amount_cents ?? 0)} charged`
                            : ' · nothing charged'}
                        </span>
                      )}
                      {!!row.reversal_shortfall_cents && row.reversal_shortfall_cents > 0 && (
                        <span className="block text-[11px] font-semibold text-rose-700">
                          {usd(row.reversal_shortfall_cents)} unrecovered
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[190px]">
                      <span className="block truncate text-sm">{row.initiated_by_email ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      {/* The first question asked about any odd top-up. ACH is
                          called out by name because 'processing' for days is
                          normal on that rail and alarming on a card. */}
                      {row.payment_method
                        ? <>
                            <Badge variant="outline" className="text-[10px] uppercase">
                              {row.payment_method === 'ach' ? 'ACH' : row.payment_method}
                            </Badge>
                            {row.payment_method_detail && (
                              <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                                {row.payment_method_detail}
                              </span>
                            )}
                          </>
                        /* No rail because no money moved — a code covered it
                           outright. Whop exposes no payment method for a $0
                           charge, so the honest answer is the promo, not a dash. */
                        : (row.settled_amount_cents === 0 && !!row.promo_discount_cents)
                          ? <Badge variant="outline" className="border-violet-200 bg-violet-50 text-[10px] uppercase text-violet-700">
                              promo
                            </Badge>
                          : <span className="text-sm text-muted-foreground/60">—</span>}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {row.whop_payment_id
                        ? <span className="block truncate font-mono text-[11px]">{row.whop_payment_id}</span>
                        : <span className="text-sm text-muted-foreground/60">—</span>}
                      {/* The top-up id, because a stuck 'pending' row is resumed
                          by sending the brand to /billing?topup=<id> — that
                          re-enters the status poll, which reconciles against
                          Whop and credits it if the money is really there. */}
                      <span
                        className="mt-0.5 block truncate font-mono text-[10px] text-muted-foreground/70"
                        title={`Top-up id ${row.id} — resume at /billing?topup=${row.id}`}
                      >
                        {row.id}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TOPUP_STATUS_STYLE[row.status]}>
                        {/* Only name the rail when Whop actually told us what it
                            was. A processing payment's method is null until it
                            settles, so the generic label is the honest one. */}
                        {/* Bank rails are named because 'processing' for days
                            is routine on them and alarming on a card. Wire is
                            separate from ACH: it clears faster and, unlike an
                            ACH debit, cannot be returned weeks later. */}
                        {row.status === 'processing' && row.payment_method === 'ach'
                          ? 'Bank transfer clearing'
                          : row.status === 'processing' && row.payment_method === 'wire'
                            ? 'Wire clearing'
                            : TOPUP_STATUS_LABEL[row.status]}
                      </Badge>
                      {row.reversal_reason && (
                        <span className="mt-1 block text-[11px] capitalize text-muted-foreground">
                          {row.reversal_reason}
                        </span>
                      )}
                      {row.status === 'failed' && row.error_message && (
                        <span className="mt-1 block max-w-[220px] truncate text-[11px] text-muted-foreground" title={row.error_message}>
                          {row.error_message}
                        </span>
                      )}
                      {/* A 'processing' row carrying an error is NOT clearing
                          normally — the money settled and crediting threw, which
                          is the one stuck state that looks identical to a
                          healthy ACH in flight. Called out in red because the
                          brand has paid and has no credit, and nothing else on
                          this screen distinguishes the two. */}
                      {row.status === 'processing' && row.error_message && (
                        <span className="mt-1 block max-w-[220px] text-[11px] font-semibold text-rose-700" title={row.error_message}>
                          Stuck — crediting failed
                          <span className="mt-0.5 block truncate font-normal text-muted-foreground">
                            {row.error_message}
                          </span>
                        </span>
                      )}
                      {/* The OTHER stuck state, and the quieter one: the money
                          never arrived at all. No rail clears in seven days, so
                          past that "processing" is not a wait — it is a payment
                          nobody can account for, and on a wire it usually means
                          the brand never actually sent it (a wire is pushed BY
                          them, which is why Whop calls this Incomplete rather
                          than failed and will not resolve it either way).
                          Flagged, never auto-failed: 'failed' would take the row
                          out of the reconcile loop, closing the one door a late
                          settlement can still come through. */}
                      {row.status === 'processing' && !row.error_message
                        && topupAgeDays(row.created_at) > TOPUP_PROCESSING_EXPECTED_DAYS && (
                        <span className="mt-1 block max-w-[220px] text-[11px] font-semibold text-rose-700">
                          Not received — {Math.floor(topupAgeDays(row.created_at))} days
                          <span className="mt-0.5 block truncate font-normal text-muted-foreground">
                            {row.last_whop_status
                              ? `Whop still says ${row.last_whop_status}${row.last_whop_substatus ? ` / ${row.last_whop_substatus}` : ''}`
                              : 'Whop has not been re-asked since this row was created'}
                          </span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {format(new Date(row.created_at), 'MMM d, yyyy')}
                      <span className="block text-[11px] text-muted-foreground">
                        {format(new Date(row.created_at), 'h:mm a')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {total > 0 && (
            <div className="mt-4">
              <DataPagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Auto refund ledger ──────────────────────────────────────────────────────

/**
 * Every wallet credit the deadline cron handed back with nobody choosing the
 * amount — a hire's deadline blowing past with zero videos delivered, refunded
 * to the brand in full, automatically. Read-only, like TopupLog above: there
 * is no admin action here, only a record of one the platform took on its own.
 * See fetchAutoRefundLog for exactly what separates this from an admin-clicked
 * refund sitting in the same ledger table.
 */
function AutoRefundLog() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<AutoRefundRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Unfiltered on purpose: the tiles are the platform-wide totals, so they
  // stay put while someone searches the list below for one brand. Its own
  // query key keeps it out of the per-page refetch.
  const { data: stats } = useQuery({
    queryKey: ['auto-refund-stats'],
    queryFn: fetchAutoRefundStats,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['auto-refund-log', page, pageSize, debounced],
    queryFn: () => fetchAutoRefundLog({ page, pageSize, search: debounced }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatTile
          label="Auto-refunded all time"
          value={usd(stats?.total_refunded_cents ?? 0)}
          tone="indigo"
          icon={RotateCcw}
          hint={
            stats
              ? `${plural(stats.total_refunds, 'refund')} · ${plural(stats.brands_refunded, 'brand')}`
              : '—'
          }
        />
        {/* Calendar month to date, not a rolling 30 days — it's the figure
            someone reconciling a month-end number is looking for. A month with
            none says when the last one was, so a zero reads as "quiet" rather
            than "is this even wired up?". */}
        <StatTile
          label="Refunded this month"
          value={usd(stats?.month_refunded_cents ?? 0)}
          tone="slate"
          icon={CalendarRange}
          hint={
            !stats ? '—'
            : stats.month_refunds > 0
              ? `${plural(stats.month_refunds, 'refund')} since ${format(new Date(stats.month_start), 'MMM d')}`
              : stats.last_refund_at
                ? `none yet — last was ${format(new Date(stats.last_refund_at), 'MMM d, yyyy')}`
                : 'none yet'
          }
        />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by brand name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Hire</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Refunded at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      No automatic refunds recorded yet.
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.ledger_id} className="cursor-pointer" onClick={() => setSelected(row)}>
                    <TableCell className="max-w-[190px]">
                      <span className="block truncate font-medium">{row.brand_name ?? 'Unnamed brand'}</span>
                      {/* {row.balance_after_cents != null && (
                        <span className="block text-[11px] tabular-nums text-muted-foreground">
                          balance → {usd(row.balance_after_cents)}
                        </span>
                      )} */}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-semibold tabular-nums text-amber-700">+{usd(row.amount_cents)}</span>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      {row.campaign_name || row.product_name
                        ? <span className="block truncate text-sm">{row.campaign_name ?? row.product_name}</span>
                        : <span className="text-sm text-muted-foreground/60">—</span>}
                      {row.creator_name && (
                        <span className="block truncate text-[11px] text-muted-foreground">{row.creator_name}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.videos_required != null
                        ? <span className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                            <Video className="h-3 w-3" /> {row.videos_delivered ?? 0}/{row.videos_required}
                          </span>
                        : <span className="text-sm text-muted-foreground/60">—</span>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {format(new Date(row.created_at), 'MMM d, yyyy')}
                      <span className="block text-[11px] text-muted-foreground">
                        {format(new Date(row.created_at), 'h:mm a')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {total > 0 && (
            <div className="mt-4">
              <DataPagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Automatic refund</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
                <p className="text-xs text-amber-700">Credited back</p>
                <p className="text-3xl font-bold tabular-nums text-amber-950">+{usd(selected.amount_cents)}</p>
                <p className="mt-1 text-xs text-amber-700">
                  {selected.brand_name ?? 'Unnamed brand'} ·{' '}
                  {format(new Date(selected.created_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Trigger">
                  <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-800">
                    <Clock className="h-3 w-3" /> Deadline passed — 0 delivered
                  </Badge>
                </Field>
                <Field label="Hire status">
                  <span className="capitalize">{selected.hire_status?.replace(/_/g, ' ') ?? '—'}</span>
                </Field>
                <Field label="Balance after">
                  {selected.balance_after_cents != null ? usd(selected.balance_after_cents) : '—'}
                </Field>
                <Field label="Brand ID">
                  <span className="font-mono text-[11px]">{selected.brand_id}</span>
                </Field>
                {selected.videos_required != null && (
                  <Field label="Delivered by deadline">
                    <span className="tabular-nums">{selected.videos_delivered ?? 0} / {selected.videos_required} videos</span>
                  </Field>
                )}
                {selected.creator_name && (
                  <Field label="Creator">
                    {selected.creator_name}
                    {selected.creator_tiktok_username && (
                      <span className="ml-1 text-xs font-normal text-muted-foreground">@{selected.creator_tiktok_username}</span>
                    )}
                  </Field>
                )}
              </div>

              {(selected.campaign_name || selected.product_name) && (
                <Field label="Campaign">
                  {selected.campaign_name ?? '—'}
                  {selected.product_name && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">· {selected.product_name}</span>
                  )}
                </Field>
              )}

              {selected.notes && (
                <Field label="Notes">
                  <p className="rounded-md border bg-muted/40 p-2 text-sm font-normal">{selected.notes}</p>
                </Field>
              )}

              <div className="grid grid-cols-1 gap-3 border-t pt-4">
                <Field label="Wallet ledger entry">
                  <span className="font-mono text-[11px]">{selected.ledger_id}</span>
                </Field>
                {selected.hire_id && (
                  <Field label="Hire ID">
                    <span className="font-mono text-[11px]">{selected.hire_id}</span>
                  </Field>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function BrandCredits() {
  const queryClient = useQueryClient();

  const { data: context, isLoading: contextLoading, error: contextError } = useQuery({
    queryKey: ['credit-grant-context', null],
    queryFn: () => fetchCreditGrantContext(null),
  });

  const { data: stats } = useQuery({ queryKey: ['credit-grant-stats'], queryFn: () => fetchCreditGrantStats(30) });

  const onGranted = () => {
    queryClient.invalidateQueries({ queryKey: ['credit-grant-log'] });
    queryClient.invalidateQueries({ queryKey: ['credit-grant-stats'] });
    queryClient.invalidateQueries({ queryKey: ['credit-grant-context'] });
    queryClient.invalidateQueries({ queryKey: ['credit-brand-search'] });
    queryClient.invalidateQueries({ queryKey: ['credit-grant-admins'] });
  };

  // Deduction — entirely separate state/queries from the grant section above,
  // none of which was touched to add this.
  const { data: deductContext, isLoading: deductContextLoading, error: deductContextError } = useQuery({
    queryKey: ['credit-deduct-context', null],
    queryFn: () => fetchCreditDeductContext(null),
  });

  const { data: deductStats } = useQuery({ queryKey: ['credit-deduct-stats'], queryFn: () => fetchCreditDeductStats(30) });

  const onDeducted = () => {
    queryClient.invalidateQueries({ queryKey: ['credit-deduct-log'] });
    queryClient.invalidateQueries({ queryKey: ['credit-deduct-stats'] });
    queryClient.invalidateQueries({ queryKey: ['credit-deduct-context'] });
    queryClient.invalidateQueries({ queryKey: ['credit-brand-search'] });
    queryClient.invalidateQueries({ queryKey: ['credit-deduct-admins'] });
  };

  const tiles = useMemo(() => ([
    {
      label: 'Granted all time', value: usd(stats?.total_granted_cents ?? 0), tone: 'violet' as const,
      icon: Wallet,
      hint: `${stats?.total_grants ?? 0} grant${stats?.total_grants === 1 ? '' : 's'}`,
    },
    {
      label: 'Last 30 days', value: usd(stats?.window_granted_cents ?? 0),
      tone: 'indigo' as const, icon: Clock,
      hint: `${stats?.window_grants ?? 0} grant${stats?.window_grants === 1 ? '' : 's'}`,
    },
    {
      label: 'Brands credited', value: String(stats?.brands_credited ?? 0),
      tone: 'slate' as const, icon: Users,
      hint: 'distinct brands, all time',
    },
    {
      label: 'Refused attempts', value: String(stats?.window_refused ?? 0),
      tone: (stats?.window_refused ?? 0) > 0 ? 'amber' as const : 'default' as const,
      icon: Ban,
      hint: 'last 30 days — worth a look if this climbs',
    },
  ]), [stats]);

  const deductTiles = useMemo(() => ([
    {
      label: 'Deducted all time', value: usd(deductStats?.total_deducted_cents ?? 0),
      tone: 'default' as const, icon: TrendingDown,
      hint: `${deductStats?.total_deductions ?? 0} deduction${deductStats?.total_deductions === 1 ? '' : 's'}`,
    },
    {
      label: 'Last 30 days', value: usd(deductStats?.window_deducted_cents ?? 0),
      tone: 'indigo' as const, icon: Clock,
      hint: `${deductStats?.window_deductions ?? 0} deduction${deductStats?.window_deductions === 1 ? '' : 's'}`,
    },
    {
      label: 'Brands affected', value: String(deductStats?.brands_affected ?? 0),
      tone: 'slate' as const, icon: Users,
      hint: 'distinct brands, all time',
    },
    {
      label: 'Refused attempts', value: String(deductStats?.window_refused ?? 0),
      tone: (deductStats?.window_refused ?? 0) > 0 ? 'amber' as const : 'default' as const,
      icon: Ban,
      hint: 'last 30 days — worth a look if this climbs',
    },
  ]), [deductStats]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Brand Credits</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Add or remove wallet credit for a brand, and audit every movement ever made.
          </p>
        </div>
        {/* {context?.admin && (
          <div className="rounded-lg border bg-card px-3 py-2 text-right">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed in as</p>
            <p className="text-sm font-medium">{context.admin.email}</p>
            <p className="text-[11px] capitalize text-muted-foreground">
              {context.admin.role_label ?? context.admin.role.replace(/_/g, ' ')}
              {context.can_grant ? ' · may grant credit' : ' · read-only'}
            </p>
          </div>
        )} */}
      </div>

      {/* ── Add / Deduct ─────────────────────────────────────────────────────
          Two top-level tabs, each holding its own KPI row + permission-gated
          form. Both contexts/stats are already fetched unconditionally above,
          so switching tabs is instant — no extra request on tab change. */}
      <Tabs defaultValue="add" className="space-y-4">
        <TabsList>
          <TabsTrigger value="add" className="gap-1.5">
            <Wallet className="h-3.5 w-3.5" /> Add Credits
          </TabsTrigger>
          <TabsTrigger value="deduct" className="gap-1.5">
            <TrendingDown className="h-3.5 w-3.5" /> Deduct Credits
          </TabsTrigger>
        </TabsList>

        <TabsContent value="add" className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {tiles.map((t) => <StatTile key={t.label} {...t} />)}
          </div>

          {contextLoading && <Skeleton className="h-64 w-full rounded-xl" />}

          {/* A failed context read means we can't establish what this admin may
              do — so nothing that grants credit renders. Failing closed is the
              only safe direction here. */}
          {!contextLoading && (contextError || !context) && (
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="flex items-start gap-2 pt-6 text-sm">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">Couldn’t confirm your credit permissions</p>
                  <p className="text-red-700">
                    {(contextError as any)?.message ?? 'The permission check failed.'} Reload, or sign out and
                    back in. The grant form stays hidden until this succeeds.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {!contextLoading && context && (
            context.can_grant
              ? <GrantPanel context={context} onGranted={onGranted} />
              : <NoPermissionCard context={context} />
          )}
        </TabsContent>

        <TabsContent value="deduct" className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {deductTiles.map((t) => <StatTile key={t.label} {...t} />)}
          </div>

          {deductContextLoading && <Skeleton className="h-64 w-full rounded-xl" />}

          {!deductContextLoading && (deductContextError || !deductContext) && (
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="flex items-start gap-2 pt-6 text-sm">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">Couldn't confirm your deduction permissions</p>
                  <p className="text-red-700">
                    {(deductContextError as any)?.message ?? 'The permission check failed.'} Reload, or sign
                    out and back in. The deduct form stays hidden until this succeeds.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {!deductContextLoading && deductContext && (
            deductContext.can_deduct
              ? <DeductPanel context={deductContext} onDeducted={onDeducted} />
              : <NoDeductPermissionCard context={deductContext} />
          )}
        </TabsContent>
      </Tabs>

      <Tabs defaultValue="log" className="space-y-4">
        <TabsList>
          <TabsTrigger value="log" className="gap-1.5">
            <ArrowUpRight className="h-3.5 w-3.5" /> Credit log
          </TabsTrigger>
          <TabsTrigger value="deductions" className="gap-1.5">
            <TrendingDown className="h-3.5 w-3.5" /> Deductions
          </TabsTrigger>
          <TabsTrigger value="topups" className="gap-1.5">
            <ShoppingCart className="h-3.5 w-3.5" /> Purchases
          </TabsTrigger>
          <TabsTrigger value="auto-refunds" className="gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" /> Auto Refunds
          </TabsTrigger>
          <TabsTrigger value="roster" className="gap-1.5">
            <Users className="h-3.5 w-3.5" /> Admin permissions
          </TabsTrigger>
        </TabsList>
        <TabsContent value="log"><GrantLog /></TabsContent>
        <TabsContent value="deductions"><DeductLog /></TabsContent>
        <TabsContent value="topups"><TopupLog /></TabsContent>
        <TabsContent value="auto-refunds"><AutoRefundLog /></TabsContent>
        <TabsContent value="roster"><PermissionRoster /></TabsContent>
      </Tabs>
    </div>
  );
}
